
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { IonSkeletonText } from '@ionic/angular/standalone';
import { ReplaySubject } from 'rxjs';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { IPojoDevice } from 'src/app/model/device/IPojoDevice';
import { ShakerFeatureDescriptor } from 'src/app/model/shaker-control/shaker-feature-descriptor';
import { ShakerProfileFeatureService } from 'src/app/services/shaker-profile/shaker-profile-feature.service';
import { SubSink } from 'subsink';

interface FeatureSelection {

  /**
   * The ShakerFeatureDescriptor
   */
  it: ShakerFeatureDescriptor;


  /**
   * The selection state
   */
  selected: boolean;
}

@Component({
  selector: 'app-shaker-control-feature-selection',
  imports: [
    MaterialImportsModule,
    IonSkeletonText,
  ],
  templateUrl: './shaker-control-feature-selection.component.html',
  styleUrls: ['./shaker-control-feature-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShakerControlFeatureSelectionComponent implements OnInit, OnDestroy {

  /**
   * The currently-selected features
   */
  @Output()
  public selectionChange = new EventEmitter<ShakerFeatureDescriptor[]>();


  /**
   * Set the currently-selected features.
   *
   * Note: features not obtainable from the shaker service will be ignored.
   */
  @Input()
  public set selection(selection: ShakerFeatureDescriptor[]) {
    this.selection$.next(selection);
  }


  /**
   * Whether the selection is required.
   *
   * Will just add a red asterisk to the label.
   */
  @Input()
  public required = false;


  @Input()
  public disabled = false;


  /**
   * The list of features to choose from
   *
   * @private
   */
  protected features: FeatureSelection[] = [];


  /**
   * The list of shakers to choose from for the pre-selection of features
   *
   * @private
   */
  private _shakers: IPojoDevice[] = [];


  /**
   * The shaker service
   */
  private shakerService = inject(ShakerProfileFeatureService);


  /**
   * Subject for the setter {@link selection}
   */
  protected selection$ = new ReplaySubject<ShakerFeatureDescriptor[]>(1);


  /**
   * RxJS subscription sink
   */
  private subsink = new SubSink();


  private changeDetector = inject(ChangeDetectorRef);


  /**
   * @private
   */
  public async ngOnInit(): Promise<void> {
    // Obtain the list of features from shakerService, then map them to FeatureSelection
    // and set the selected state to true if the fature is in selection$, false otherwise

    this.features = (await this.shakerService.getFeatures()).map(feature => ({
      it: feature,
      selected: false
    }));

    this.subsink.sink = this.selection$.subscribe(selection => {
      this.features.forEach(feature => {
        feature.selected = selection.some(
          selectedFeature => selectedFeature.id === feature.it.id
        );
      });

      this.changeDetector.markForCheck();
    });

    this._shakers = await this.shakerService.getShakers();
    this.changeDetector.markForCheck();
  }


  /**
   * @private
   */
  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }


  /**
   * Update the selection change of feature and emit the new selection
   *
   * @param feature The feature to update
   * @param checked The new selection state
   * @private
   */
  public _setFeatureSelection(feature: FeatureSelection, checked: boolean): void {
    feature.selected = checked;
    this.emitSelectedFeatures();
  }


  /**
   * Emit the currently-selected features
   */
  private emitSelectedFeatures(): void {
    this.selectionChange.emit(
      this.features.filter(feature => feature.selected)
        .map(feature => feature.it)
    );
  }
}
